import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { gamesAPI, tickerAPI, gameplayerAPI, playertoolsAPI, matchMetaAPI, projectsAPI, roundsAPI } from '../../services/api';
import { useTickerSocket } from '../../hooks/useTickerSocket';
import { calculateMinute, clockAction } from '../../utils/matchClock';
import TickerHeader from './TickerHeader';
import TickerTab from './TickerTab';
import LineupTab from './LineupTab';
import WidgetEmbed from './WidgetEmbed';
import ReportGenerator from './ReportGenerator';
import './ticker.css';

const TABS = [
    { key: 'ticker', label: 'Ticker', icon: '📡' },
    { key: 'lineup', label: 'Aufstellung', icon: '👥' },
    { key: 'report', label: 'Bericht', icon: '📄' },
    { key: 'embed', label: 'Widget', icon: '🌐' },
];

export default function GameTickerPage() {
    const { projectId, roundId, gameId } = useParams();

    const [game, setGame] = useState(null);
    const [project, setProject] = useState(null);
    const [round, setRound] = useState(null);
    const [events, setEvents] = useState([]);
    const [lineup, setLineup] = useState([]);
    const [availablePlayers, setAvailablePlayers] = useState([]);
    const [meta, setMeta] = useState(null);
    const [displayMinute, setDisplayMinute] = useState(0);
    const [activeTab, setActiveTab] = useState('ticker');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const metaRef = useRef(null);

    useEffect(() => {
        metaRef.current = meta;
        setDisplayMinute(calculateMinute(meta));
    }, [meta]);

    useEffect(() => {
        const interval = setInterval(() => {
            const m = metaRef.current;
            if (m?.clockRunning && !m?.clockPaused) {
                setDisplayMinute(calculateMinute(m));
            }
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    useTickerSocket(gameId, {
        onEvent: useCallback((data) => {
            setEvents(prev => {
                if (prev.find(e => e._id === data._id)) return prev;
                return [...prev, data];
            });
        }, []),
        onUpdate: useCallback((data) => {
            setEvents(prev => prev.map(e => e._id === data._id ? data : e));
        }, []),
        onDelete: useCallback(({ id }) => {
            setEvents(prev => prev.filter(e => e._id !== id));
        }, []),
        onScore: useCallback((data) => {
            setMeta(prev => prev ? { ...prev, scoreFT: data.scoreFT, scoreHT: data.scoreHT } : prev);
        }, []),
        onStatus: useCallback((data) => {
            setMeta(prev => prev ? { ...prev, status: data.status } : prev);
        }, []),
    });

    useEffect(() => {
        loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameId]);

    const loadAll = async () => {
        setLoading(true);
        setError('');
        try {
            const [gameRes, tickerRes, lineupRes, metaRes] = await Promise.all([
                gamesAPI.getById(gameId),
                tickerAPI.getByGame(gameId),
                gameplayerAPI.getByGame(gameId),
                matchMetaAPI.get(gameId),
            ]);

            const g = gameRes.data;
            setGame(g);
            setEvents(tickerRes.data);
            setLineup(lineupRes.data || []);
            setMeta(metaRes.data);

            if (projectId) {
                projectsAPI.getById(projectId).then(r => setProject(r.data)).catch(() => {});
            }
            if (roundId) {
                roundsAPI.getById(roundId).then(r => setRound(r.data)).catch(() => {});
            }

            if (g?.pid && g?.hid && g?.aid) {
                const pid = g.pid?._id || g.pid;
                const hid = g.hid?._id || g.hid;
                const aid = g.aid?._id || g.aid;
                const [homeRes, awayRes] = await Promise.all([
                    playertoolsAPI.getAll({ pid, tid: hid }),
                    playertoolsAPI.getAll({ pid, tid: aid }),
                ]);
                const homeList = homeRes.data?.playertools || [];
                const awayList = awayRes.data?.playertools || [];
                setAvailablePlayers([...homeList, ...awayList]);
            }
        } catch (err) {
            setError('Fehler beim Laden der Spieldaten');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreated = (newEvent) => {
        setEvents(prev => {
            if (prev.find(e => e._id === newEvent._id)) return prev;
            return [...prev, newEvent];
        });
    };

    const handleDelete = (id) => setEvents(prev => prev.filter(e => e._id !== id));
    const handleUpdate = (updated) => setEvents(prev => prev.map(e => e._id === updated._id ? updated : e));

    const handleClockAction = async (action) => {
        const payload = clockAction(action, meta || {});
        if (!payload) return;
        try {
            const res = await matchMetaAPI.update(gameId, payload);
            setMeta(res.data);

            const autoTexts = {
                kickoff:     { type: 9, text: 'Anpfiff!' },
                halftime:    { type: 6, text: `Halbzeit – Stand: ${meta?.scoreFT?.home ?? 0}:${meta?.scoreFT?.away ?? 0}` },
                second_half: { type: 9, text: 'Wiederanpfiff – 2. Halbzeit' },
                fulltime:    { type: 7, text: `Abpfiff – Endstand: ${meta?.scoreFT?.home ?? 0}:${meta?.scoreFT?.away ?? 0}` },
            };
            if (autoTexts[action]) {
                const { type, text } = autoTexts[action];
                await tickerAPI.create({
                    gid: gameId,
                    type,
                    minute: action === 'halftime' ? 45 : action === 'fulltime' ? 90 : displayMinute,
                    half: action === 'second_half' ? 2 : meta?.currentHalf || 1,
                    textRaw: text,
                    textPublished: text,
                    published: true,
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    if (loading) {
        return (
            <div className="ticker-workspace flex items-center justify-center">
                <div className="text-center">
                    <span className="loading loading-spinner loading-lg text-green-500" />
                    <p className="mt-3 text-slate-500">Lade Spieldaten…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="ticker-workspace p-6">
                <div className="ticker-modal max-w-md mx-auto">
                    <p className="text-red-400 mb-3">⚠️ {error}</p>
                    <button type="button" className="ticker-clock-btn ticker-clock-btn--go" onClick={loadAll}>Nochmal versuchen</button>
                </div>
            </div>
        );
    }

    return (
        <div className="ticker-workspace">
            <TickerHeader
                game={game}
                project={project}
                round={round}
                meta={meta}
                displayMinute={displayMinute}
                onClockAction={handleClockAction}
                onMetaUpdate={setMeta}
            />

            <nav className="ticker-tabs">
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        className={`ticker-tab ${activeTab === tab.key ? 'ticker-tab--active' : ''}`}
                    >
                        {tab.icon} {tab.label}
                    </button>
                ))}
            </nav>

            <div className="ticker-main">
                {activeTab === 'ticker' && (
                    <div className="ticker-tab-panel flex flex-col flex-1 min-h-0">
                        <TickerTab
                            gameId={gameId}
                            game={game}
                            events={events}
                            lineup={lineup}
                            availablePlayers={availablePlayers}
                            meta={meta}
                            displayMinute={displayMinute}
                            onCreated={handleCreated}
                            onDelete={handleDelete}
                            onUpdate={handleUpdate}
                            onMetaUpdate={setMeta}
                        />
                    </div>
                )}

                {activeTab === 'lineup' && (
                    <div className="ticker-tab-panel ticker-tab-panel--wide ticker-secondary-panel">
                        <LineupTab
                            game={game}
                            gameId={gameId}
                            lineup={lineup}
                            availablePlayers={availablePlayers}
                            onLineupChange={setLineup}
                        />
                    </div>
                )}

                {activeTab === 'report' && (
                    <div className="ticker-tab-panel ticker-secondary-panel">
                        <ReportGenerator gameId={gameId} events={events} />
                    </div>
                )}

                {activeTab === 'embed' && (
                    <div className="ticker-tab-panel ticker-secondary-panel">
                        <WidgetEmbed gameId={gameId} />
                    </div>
                )}
            </div>
        </div>
    );
}
